export const DEFAULT_WORKING_DAYS = [0, 2, 3, 4, 5, 6];
export const DEFAULT_SUNDAY_START_HOUR = 11;

export const DAY_LABELS = [
  'Pazar',
  'Pazartesi',
  'Salı',
  'Çarşamba',
  'Perşembe',
  'Cuma',
  'Cumartesi'
];

export interface TeamMemberSchedule {
  workingDays?: number[] | string;
  sundayStartHour?: number;
}

export function normalizeWorkingDays(value?: number[] | string | null): number[] {
  if (Array.isArray(value) && value.length > 0) {
    return value.map(Number).filter((d) => !Number.isNaN(d) && d >= 0 && d <= 6);
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(Number).filter((d) => !Number.isNaN(d) && d >= 0 && d <= 6);
      }
    } catch {
      // ignore invalid JSON
    }
  }

  return [...DEFAULT_WORKING_DAYS];
}

export function parseWorkingDays(member?: TeamMemberSchedule | null): number[] {
  return normalizeWorkingDays(member?.workingDays);
}

export function getDayOfWeek(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00+03:00`).getDay();
}

export function isWorkingDay(member: TeamMemberSchedule | null | undefined, dateStr: string): boolean {
  return parseWorkingDays(member).includes(getDayOfWeek(dateStr));
}

export function getStartHour(member: TeamMemberSchedule | null | undefined, dateStr: string): number {
  if (getDayOfWeek(dateStr) === 0) {
    return member?.sundayStartHour ?? DEFAULT_SUNDAY_START_HOUR;
  }
  return 9;
}

export function generateTimeSlots(member: TeamMemberSchedule | null | undefined, dateStr: string): string[] {
  if (!isWorkingDay(member, dateStr)) {
    return [];
  }

  const startHour = getStartHour(member, dateStr);
  const slots: string[] = [];

  for (let hour = startHour; hour < 18; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  return slots.filter((slot) => slot !== '18:00');
}

export function formatWorkingDaysSummary(member?: TeamMemberSchedule | null): string {
  const days = parseWorkingDays(member);
  return days.map((day) => DAY_LABELS[day]).join(', ');
}

export function getAdminDaySlotTimes(
  dateStr: string,
  members: Array<TeamMemberSchedule & { id?: number; isActive?: boolean }>,
  filterTeamMemberId?: string
): string[] {
  if (filterTeamMemberId && filterTeamMemberId !== 'all') {
    const member = members.find((m) => m.id?.toString() === filterTeamMemberId);
    return generateTimeSlots(member, dateStr);
  }

  const allSlots = new Set<string>();
  members
    .filter((member) => member.isActive !== false)
    .forEach((member) => {
      generateTimeSlots(member, dateStr).forEach((slot) => allSlots.add(slot));
    });

  if (allSlots.size === 0) {
    return generateTimeSlots(null, dateStr);
  }

  return Array.from(allSlots).sort();
}

export function getTimelineStartHour(slotTimes: string[]): number {
  if (slotTimes.length === 0) {
    return 9;
  }
  return parseInt(slotTimes[0].split(':')[0], 10);
}
